import type React from "react";
import { useMemo, useRef, useState, useEffect } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { FileItem } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/FileUpload/FileItem";
import { FileItemSkeleton } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/FileUpload/FileItemSkeleton";
import { FileToast } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/FileUpload/FileToast";
import { processFile } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/FileUpload/processFile";
import {
    StyledInput,
    StyledLabel,
    StyledLoadingSpinner,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/FileUpload/styles";
import { aiAgentKnowledgeBaseQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/queryKeyFactory";
import type {
    KBFile,
    KnowledgeBaseResponse,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/types";

const MAX_FILE_SIZE_MB = 25;
export const FILES_LIMIT = 10;

const bytesToMegabytes = (bytes: number) => {
    return bytes / (1024 * 1024);
};

const validateFileSize = (file: File): boolean => {
    const fileSizeInMB = bytesToMegabytes(file.size);
    return fileSizeInMB <= MAX_FILE_SIZE_MB;
};

type Props = {
    value: KBFile[];
    productOfferings: string[];
};

export const FileUpload: React.FC<Props> = ({ value, productOfferings }) => {
    const { state, params } = useCurrentStateAndParams<{ domain: string | undefined; [key: string]: unknown }>();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const isDisabled = useMemo(() => value.length === FILES_LIMIT, [value]);

    const productOfferingsRef = useRef<string[]>(productOfferings);

    useEffect(() => {
        productOfferingsRef.current = productOfferings;
    }, [productOfferings]);

    const getPOValues = () => {
        return productOfferingsRef.current;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isDisabled || isLoading) {
            return;
        }

        const files = event.target.files;

        if (files && files.length > 0) {
            setIsLoading(true);
            const file = files[0];

            if (!validateFileSize(file)) {
                setIsError(true);
                setIsLoading(false);
                if (inputRef.current) {
                    inputRef.current.value = "";
                }
                return;
            }
            setIsError(false);

            void processFile({
                file,
                queryClient,
                getPOValues,
                onPOUpdated: (updatedPO) => {
                    productOfferingsRef.current = updatedPO;
                },
                onFileUploaded: () => {
                    setIsLoading(false);
                },
                showMessage: () =>
                    dispatch(
                        showSuccessToast(
                            <FileToast
                                stateName={state.name}
                                params={params}
                            />,
                        ),
                    ),
            });

            if (inputRef.current) {
                inputRef.current.value = "";
            }
        }
    };

    const handleSuccessfulDelete = (id: string) => {
        AIAgentTrackingService.kb.trackFileRemove();
        queryClient.setQueryData(
            aiAgentKnowledgeBaseQueryKeys.customization(),
            (oldData: KnowledgeBaseResponse): KnowledgeBaseResponse => ({
                ...oldData,
                files: oldData.files.filter((file) => file.id !== id),
            }),
        );
    };

    const color = isDisabled ? colorsPalettes.carbon[100] : colorsPalettes.blue[400];

    return (
        <>
            <Tooltip
                content={translate("si.agent_knowledge_base.modal.file.input.disabled.tooltip", { limit: FILES_LIMIT })}
                enabled={isDisabled}
            >
                <StyledLabel
                    isError={isError}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                >
                    {isLoading ? (
                        <>
                            <StyledLoadingSpinner color={colorsPalettes.blue[400]} />
                            <Text
                                fontVariant="button"
                                color={color}
                            >
                                {translate("si.agent_knowledge_base.modal.file.input.loading.text")}
                            </Text>
                        </>
                    ) : (
                        <>
                            <SWReactIcons
                                iconName="upload-file"
                                size="xs"
                                iconColor={color}
                            />
                            <Text
                                fontVariant="button"
                                color={color}
                            >
                                {translate("si.agent_knowledge_base.modal.file.input.text")}
                            </Text>
                        </>
                    )}
                    <StyledInput
                        type="file"
                        ref={inputRef}
                        onChange={handleInputChange}
                        accept=".pdf"
                        disabled={isDisabled}
                    />
                </StyledLabel>
            </Tooltip>
            {isError ? (
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.red["400_P"]}
                    display="block"
                    margin="4px 0 0"
                >
                    {translate("si.agent_knowledge_base.modal.file.input.validation.size", { size: MAX_FILE_SIZE_MB })}
                </Text>
            ) : null}
            <Stack margin="4px 0 0">
                {value.map((file) => (
                    <FileItem
                        key={file.id}
                        file={file}
                        onDeleteSuccess={() => handleSuccessfulDelete(file.id)}
                        dataAutomation="file-item"
                    />
                ))}
                {isLoading ? <FileItemSkeleton /> : null}
            </Stack>
        </>
    );
};
